import { isSameDay } from 'date-fns'
import PropTypes from 'prop-types'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { QUERY_KEYS } from 'constants/constants'
import trackEvents from '../../constants/track-events'
import { useNotificationContext, usePartnerContext } from '../../context'
import { mutation, query } from '../../graphql'
import { trackEvent } from '../../helpers/analytics'
import {
  computeExpiresAt,
  formatAssessmentDeadline,
  getExpiresAfterDays,
  isDeadlineRunning,
  pickerDateToExpiresAt,
  toPickerDate
} from '../../helpers/assessment-deadline'
import { useCreateLog } from '../../hooks/use-create-log'
import { useMailTemplates } from '../../hooks/use-mail-templates'
import { useMutationAndUpdateQuery } from '../../hooks/use-mutation-and-update-query'
import Button from '../button'
import EditMailTemplate from '../mail-template/edit-mail-template'
import Datepicker from '../react-datepicker/datepicker'
import InfoCallout from '../tailwind/InfoCallout'
import Modal from '../tailwind/Modal'

const TRANSLATION_LOCATION = 'components.career.send_reminder.'

const SendReminder = ({ space, refetchSpace, career, open, setOpen }) => {
  const { t, i18n } = useTranslation()
  const { partner } = usePartnerContext()
  const { success } = useNotificationContext()

  const { createSpaceHistoryLog } = useCreateLog()

  const partnerMutation = useMutationAndUpdateQuery({
    queryKey: QUERY_KEYS.updatePartner(partner.id),
    mutation: 'updatePartner'
  })

  const careerMutation = useMutationAndUpdateQuery({
    queryKey: QUERY_KEYS.updateCareer(career?.id),
    mutation: 'updateCareer'
  })

  const { mailTemplateMacrosReminder, mailTemplateReminder } = useMailTemplates(
    {
      career
    }
  )

  const [isLoading, setIsLoading] = useState(false)

  // Die Erinnerung lässt eine laufende Frist unverändert. Nur ein im Picker
  // anders gewählter Tag wird gespeichert. Ist die Frist abgelaufen, schlägt
  // der Dialog heute plus die Bearbeitungszeit der Stelle vor.
  const deadlineRunning = isDeadlineRunning(space.expiresAt)
  const defaultDay = useMemo(
    () =>
      toPickerDate(
        deadlineRunning
          ? space.expiresAt
          : computeExpiresAt(
              getExpiresAfterDays(partner.app_settings, career?.app_settings)
            )
      ),
    [
      deadlineRunning,
      space.expiresAt,
      partner.app_settings,
      career?.app_settings
    ]
  )
  const [pickedDay, setPickedDay] = useState(null)

  // Bei jedem Öffnen neu vom gespeicherten Fristende ausgehen
  useEffect(() => {
    if (open) setPickedDay(null)
  }, [open])

  const selectedDay = pickedDay || defaultDay
  const changesDeadline =
    !deadlineRunning || (pickedDay && !isSameDay(pickedDay, defaultDay))
  const selectedDeadline = formatAssessmentDeadline(
    pickerDateToExpiresAt(selectedDay),
    i18n.language
  )

  let deadlineHint = 'deadline_unchanged_hint'
  if (!deadlineRunning) deadlineHint = 'deadline_expired_hint'
  else if (changesDeadline) deadlineHint = 'deadline_changed_hint'
  const [showEditMailTemplateReminder, setShowEditMailTemplateReminder] =
    useState(false)

  const remindTalent = async () => {
    setIsLoading(true)

    const event = { context: 'SYSTEM', status: 'REMINDED' }

    try {
      if (changesDeadline) {
        await mutation({
          mutation: 'updateSpace',
          input: {
            id: space.id,
            expiresAt: pickerDateToExpiresAt(selectedDay)
          }
        })
      }

      await createSpaceHistoryLog({
        space_id: space.id,
        career_id: space.career_id,
        ...event
      })

      await query({
        query: 'inviteTalent',
        variables: {
          space_id: space.id,
          email: space.email,
          language: space.language,
          career_id: space.career_id,
          partner_id: space.partner_id,
          email_template: 'remind'
        }
      })
    } catch (err) {
      setIsLoading(false)
      return
    }

    refetchSpace()
    setTimeout(() => {
      trackEvent(trackEvents.REMIND_TALENT)
      success(t(TRANSLATION_LOCATION + 'send_reminder_success'))
      setOpen(false)
      setIsLoading(false)
    }, 1000 * 0.5)
  }

  return (
    <>
      <Modal open={open} setOpen={() => setOpen(false)} size='3xl'>
        <div className='mt-3 text-center sm:mt-5'>
          <h2 className='text-3xl font-extrabold tracking-tight text-gray-900 sm:text-4xl'>
            {t(TRANSLATION_LOCATION + 'send_reminder_modal_title')}
          </h2>
        </div>
        <div className='mx-4 mb-8 mt-12 flex items-center justify-between'>
          <div>
            <span className='block text-sm font-medium text-gray-700'>
              {t(TRANSLATION_LOCATION + 'mail_template_dropdown_label')}
            </span>
            <span className='mt-1 block text-sm font-semibold text-gray-900'>
              {mailTemplateReminder.subject}
            </span>
          </div>
          <Button
            appearance='xs'
            text={t(TRANSLATION_LOCATION + 'edit_mail_template_action')}
            onClick={() => {
              setOpen(false)
              setTimeout(
                () => setShowEditMailTemplateReminder(true),
                1000 * 0.4
              )
            }}
          />
        </div>
        <div className='mx-4 mt-8'>
          <p className='block text-sm font-medium text-gray-700'>
            {t(TRANSLATION_LOCATION + 'reset_expired_title')}
          </p>
          <div className='mt-2'>
            <Datepicker
              selected={selectedDay}
              onChange={(date) => setPickedDay(date)}
              minDate={toPickerDate(new Date().toISOString())}
            />
          </div>
          <InfoCallout
            tone={changesDeadline ? 'info' : 'neutral'}
            className='mt-4'
          >
            {t(TRANSLATION_LOCATION + deadlineHint, {
              date: selectedDeadline
            })}
          </InfoCallout>
        </div>
        <div className='mt-12 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3 md:flex md:justify-center'>
          <Button
            text={t(TRANSLATION_LOCATION + 'send_reminder_modal_cancel_action')}
            onClick={() => setOpen(false)}
            appearance='w-full-gray'
            className='md:w-80'
          />
          <Button
            text={t(TRANSLATION_LOCATION + 'send_reminder_modal_submit_action')}
            onClick={remindTalent}
            appearance='w-full-base'
            className='md:w-80'
            isLoading={isLoading}
          />
        </div>
      </Modal>
      <EditMailTemplate
        open={showEditMailTemplateReminder}
        setOpen={() => {
          setShowEditMailTemplateReminder(false)
          setTimeout(() => setOpen(true), 1000 * 0.4)
        }}
        mailTemplate={mailTemplateReminder}
        mailTemplateMacros={mailTemplateMacrosReminder}
        handleSubmit={({ result, onSuccess, onError }) => {
          if (career) {
            const app_settings = career.app_settings
            app_settings.mail_template_reminder = result

            const input = { id: career.id, app_settings }
            careerMutation.mutate({ input }, { onSuccess, onError })
          } else {
            const app_settings = partner.app_settings || {}
            app_settings.mail_template_reminder = result

            const input = { id: partner.id, app_settings }
            partnerMutation.mutate({ input }, { onSuccess, onError })
          }
        }}
      />
    </>
  )
}

SendReminder.propTypes = {
  space: PropTypes.object.isRequired,
  refetchSpace: PropTypes.func.isRequired,
  career: PropTypes.object,
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired
}

SendReminder.defaultProps = {
  career: null
}

export default SendReminder
